import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Toolbar, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Chip, FormControlLabel, Checkbox, Select, MenuItem,
  FormControl, InputLabel, Tooltip, CircularProgress, Alert, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  AdminPanelSettings as AdminIcon, Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/sideBar/sideBar';
import Header from '../Components/header/Header';
import {
  listUsers, addUser, updateUser, deleteUser, ALL_PERMISSIONS,
} from '../services/userService';

const drawerWidth = 260;

const DEFAULT_PERMISSIONS = ALL_PERMISSIONS.map((p) => p.key);

const emptyForm = {
  email: '',
  name: '',
  role: 'user',
  permissions: [...DEFAULT_PERMISSIONS],
};

export default function ManageUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data?.users || []);
    } catch (err) {
      showSnack(err.message || 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openAdd = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    setEditTarget(user);
    setForm({
      email: user.email,
      name: user.name || '',
      role: user.role || 'user',
      permissions: Array.isArray(user.permissions) ? user.permissions : DEFAULT_PERMISSIONS,
    });
    setDialogOpen(true);
  };

  const openDelete = (user) => {
    setDeleteTarget(user);
    setDeleteDialogOpen(true);
  };

  const handlePermissionToggle = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const handleSave = async () => {
    if (!form.email.trim()) return showSnack('Email is required', 'error');
    setSaving(true);
    try {
      if (editTarget) {
        await updateUser(form.email, form.role, form.permissions, form.name);
        showSnack('User updated successfully');
      } else {
        await addUser(form.email, form.name, form.role, form.permissions);
        showSnack('User added successfully');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      showSnack(err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.email);
      showSnack('User removed');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      showSnack(err.message || 'Delete failed', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar drawerWidth={drawerWidth} onNavigate={(path) => navigate(path)} />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f9f9f9', minHeight: '100vh' }}>
        <Header />
        <Toolbar />
        <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c4a52' }}>
          Manage Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#4D99A3', '&:hover': { backgroundColor: '#3d7a84' } }}
        >
          Add User
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: '#4D99A3' }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f7f8' }}>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Accessible Tabs</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No users found. Click "Add User" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.email} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.role === 'admin' ? <AdminIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                        label={user.role === 'admin' ? 'Admin' : 'User'}
                        size="small"
                        sx={{
                          backgroundColor: user.role === 'admin' ? '#e8f4f6' : '#f0f0f0',
                          color: user.role === 'admin' ? '#2c7a87' : '#666',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <Typography variant="caption" sx={{ color: '#4D99A3', fontStyle: 'italic' }}>
                          All tabs (admin)
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {ALL_PERMISSIONS.map((p) => {
                            const granted = Array.isArray(user.permissions) && user.permissions.includes(p.key);
                            return (
                              <Chip
                                key={p.key}
                                label={p.label}
                                size="small"
                                variant={granted ? 'filled' : 'outlined'}
                                sx={{
                                  fontSize: '0.68rem',
                                  backgroundColor: granted ? '#e0f2f3' : 'transparent',
                                  color: granted ? '#2c7a87' : '#aaa',
                                  borderColor: granted ? '#4D99A3' : '#ddd',
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(user)} sx={{ color: '#4D99A3' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => openDelete(user)} sx={{ color: '#e57373' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editTarget ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Email Address"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              disabled={!!editTarget}
              fullWidth
              size="small"
              helperText="User must sign in via Microsoft with this email"
            />
            <TextField
              label="Display Name (optional)"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                label="Role"
                onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            {form.role === 'user' && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#444' }}>
                  Tab Permissions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {ALL_PERMISSIONS.map((p) => (
                    <FormControlLabel
                      key={p.key}
                      control={
                        <Checkbox
                          checked={form.permissions.includes(p.key)}
                          onChange={() => handlePermissionToggle(p.key)}
                          size="small"
                          sx={{ color: '#4D99A3', '&.Mui-checked': { color: '#4D99A3' } }}
                        />
                      }
                      label={<Typography variant="body2">{p.label}</Typography>}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {form.role === 'admin' && (
              <Alert severity="info" sx={{ mt: 0 }}>
                Admins have access to all tabs and can manage users.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#4D99A3', '&:hover': { backgroundColor: '#3d7a84' } }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : editTarget ? 'Save Changes' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove User?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will revoke access for <strong>{deleteTarget?.email}</strong>. They will no longer be able to log in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}
